import React, {useCallback, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {addDoc, collection, serverTimestamp} from 'firebase/firestore';
import {getDownloadURL, ref, uploadBytes} from 'firebase/storage';
import {db, storage} from '../../firebase.config';
import AdminNavbar from "./AdminNavbar";

const PROJECT_TYPES = ['conversion', 'extension', 'new build', 'renovation', 'landscape'];

function AddProject() {
    const [header, setHeader] = useState('');
    const [files, setFiles] = useState([]);
    const [mainImageIndex, setMainImageIndex] = useState(null);
    const [description, setDescription] = useState('');
    const [projectType, setProjectType] = useState('');

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const onDrop = useCallback(function(acceptedFiles) {
        const newFiles = acceptedFiles.map(function(file) {
            return Object.assign(file, {
                preview: URL.createObjectURL(file)
            });
        });
        setFiles(function(prev) {
            return [...prev, ...newFiles];
        });
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
        },
        maxSize: 12 * 1024 * 1024
    });

    function removeFile(index) {
        setFiles(function(prev) {
            const newFiles = [...prev];
            URL.revokeObjectURL(newFiles[index].preview);
            newFiles.splice(index, 1);
            return newFiles;
        });

        if (mainImageIndex === index) {
            setMainImageIndex(null);
        } else if (mainImageIndex !== null && mainImageIndex > index) {
            setMainImageIndex(mainImageIndex - 1);
        }
    }

    function validateForm() {
        if (!header.trim()) {
            setError('Header is required');
            return false;
        }
        if (files.length === 0) {
            setError('At least one image is required');
            return false;
        }
        if (files.length > 5) {
            setError('You can upload a maximum of 5 images');
            return false;
        }
        if (!projectType) {
            setError('Project type is required');
            return false;
        }
        if (!PROJECT_TYPES.includes(projectType)) {
            setError('Invalid project type');
            return false;
        }
        return true;
    }

    async function uploadImages() {
        return await Promise.all(
            files.map(async function(file, index) {
                const storageRef = ref(storage, `projects/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);
                return {
                    url,
                    ordinal: index,
                    mainImage: mainImageIndex === index
                };
            })
        );
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!validateForm()) return;

        setLoading(true);
        try {
            const imageUrls = await uploadImages();

            await addDoc(collection(db, 'projects'), {
                header: header.trim(),
                images: imageUrls,
                description: description.trim() || null,
                projectType,
                createdAt: serverTimestamp()
            });

            setSuccess('Project added successfully');

            setHeader('');
            files.forEach(function(file) {
                URL.revokeObjectURL(file.preview);
            });
            setFiles([]);
            setMainImageIndex(null);
            setDescription('');
            setProjectType('');
        } catch (err) {
            console.error('Error adding project:', err);
            if (err.code === 'storage/unauthorized') {
                setError('Invalid image URL');
            } else {
                setError('Failed to add project. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }

    function handleHeaderChange(e) {
        setHeader(e.target.value);
    }

    function handleDescriptionChange(e) {
        setDescription(e.target.value);
    }

    function handleProjectTypeChange(e) {
        setProjectType(e.target.value);
    }

    return (
      <div className={"bg-gray-50"}>
          <AdminNavbar/>
          <div className={"min-h-screen flex justify-center w-screen relative z-30 mx-auto px-4 mt-[110px] py-16 pb-36"}>
              <div className="container">
                  <div className="flex justify-between items-center mb-6">
                      <h1 className="text-[28px] font-semibold text-[#081542]">Add New Project</h1>
                  </div>

                  <div className="bg-white p-6 rounded shadow">
                      <form onSubmit={handleSubmit}>
                          <div className="mb-4">
                              <label className="block text-gray-700 font-medium mb-2" htmlFor="header">
                                  Project Header *
                              </label>
                              <input
                                type="text"
                                id="header"
                                value={header}
                                onChange={handleHeaderChange}
                                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                                placeholder="Enter project header"
                              />
                          </div>

                          <div className="mb-4">
                              <label className="block text-gray-700 font-medium mb-2">
                                  Project Images *
                              </label>
                              <div
                                {...getRootProps()}
                                className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors
                                ${isDragActive
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-300 hover:border-gray-400'
                                }`}
                              >
                                  <input {...getInputProps()} />
                                  {isDragActive ? (
                                    <p className="text-blue-500">Drop the images here...</p>
                                  ) : (
                                    <div>
                                        <p className="text-gray-600">Drag & drop images here, or click to select</p>
                                        <p className="text-gray-400 text-sm mt-1">Max 12MB per image</p>
                                    </div>
                                  )}
                              </div>

                              {files.length > 0 && (
                                <div className="mt-4">
                                    <p className="text-sm text-gray-500 mb-2">
                                        Click the circle to set an image as the main image for the projects list.
                                    </p>
                                    <div className="flex flex-wrap gap-4 pt-2">
                                        {files.map(function (file, index) {
                                            const isMain = mainImageIndex === index;
                                            return (
                                              <div key={index} className="relative p-2">
                                                  <img
                                                    src={file.preview}
                                                    alt={`Preview ${index + 1}`}
                                                    className="h-36 aspect-video object-cover rounded"
                                                  />
                                                  <button
                                                    type="button"
                                                    onClick={function () {
                                                        setMainImageIndex(isMain ? null : index);
                                                    }}
                                                    className={`absolute top-4 left-4 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                                                    ${isMain
                                                      ? 'bg-[#081542] border-[#081542]'
                                                      : 'bg-white border-gray-300 hover:border-[#081542]'}`}
                                                  >
                                                      {isMain && (
                                                        <svg className="w-4 h-4 text-white" fill="currentColor"
                                                             viewBox="0 0 20 20">
                                                            <path fillRule="evenodd"
                                                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                                  clipRule="evenodd"/>
                                                        </svg>
                                                      )}
                                                  </button>
                                                  {isMain && (
                                                    <span
                                                      className="absolute bottom-4 left-4 bg-[#081542] text-white text-xs px-2 py-1 rounded">
                                                    Main
                                                </span>
                                                  )}
                                                  <button
                                                    type="button"
                                                    onClick={function () {
                                                        removeFile(index);
                                                    }}
                                                    className="absolute -top-0 -right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                                                  >
                                                      x
                                                  </button>
                                                  <span
                                                    className="absolute bottom-1 right-1 bg-black bg-opacity-80 text-white text-base px-2 rounded">
                                                  {index + 1}
                                              </span>
                                              </div>
                                            );
                                        })}
                                    </div>
                                </div>
                              )}
                          </div>

                          <div className="mb-4">
                              <label className="block text-gray-700 font-medium mb-2" htmlFor="description">
                                  Description (Optional)
                              </label>
                              <textarea
                                id="description"
                                value={description}
                                onChange={handleDescriptionChange}
                                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                                placeholder="Enter project description"
                                rows={4}
                              />
                          </div>

                          <div className="mb-6">
                              <label className="block text-gray-700 font-medium mb-2" htmlFor="projectType">
                                  Project Type *
                              </label>
                              <select
                                id="projectType"
                                value={projectType}
                                onChange={handleProjectTypeChange}
                                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                              >
                                  <option value="">Select a project type</option>
                                  {PROJECT_TYPES.map(function (type) {
                                      return (
                                        <option key={type} value={type}>
                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                        </option>
                                      );
                                  })}
                              </select>
                          </div>

                          {error && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                                {error}
                            </div>
                          )}

                          {success && (
                            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                                {success}
                            </div>
                          )}

                          <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-2 px-4 border-r-4 rounded font-medium text-white transition-colors
                            ${loading
                              ? 'bg-[#0d1f79] cursor-not-allowed'
                              : 'bg-[#081542] hover:bg-[#0d1f79]'
                            }`}
                          >
                              {loading ? 'Adding Project...' : 'Add Project'}
                          </button>
                      </form>
                  </div>
              </div>
          </div>
      </div>
    );
}

export default AddProject;
